import { AlertTriangle, Home } from 'lucide-react';

interface Props {
  error?: Error;
}

export const FallbackUI = ({ error }: Props) => {
  const handleRefresh = () => {
    window.location.reload();
  };

  const handleHome = () => {
    // Use a full navigation instead of useNavigate() so the fallback works
    // even when rendered outside a Router (e.g. from the top-level
    // ErrorBoundary). This also forces a fresh load of the app.
    window.location.assign('/');
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-6">
      <div className="w-full max-w-[450px]">
        {/* SLC Logo - Keep Centered */}
        <div className="flex justify-center mb-6">
          <img
            src="/logos/slc-logo-login.png"
            alt="SLC Logo"
            className="h-10 sm:h-12 w-auto object-contain"
          />
        </div>

        {/* Error Card */}
        <div
          className="
            w-full max-w-[460px]
            bg-white
            rounded-xl
            border border-slate-200
            shadow-[0_4px_20px_rgba(0,0,0,0.08)]
            px-5
            py-5
            sm:px-6
            sm:py-6
          "
        >
          {/* Error Content */}
          <div className="flex items-start gap-5">
            {/* Error Icon */}
            <div className="flex-shrink-0 pt-1">
              <AlertTriangle className="w-6 h-6 text-red-500" strokeWidth={2} />
            </div>

            {/* Error Message */}
            <div className="flex-1">
              <h1
                className="
                  text-lg
                  sm:text-xl
                  font-semibold
                  text-slate-900
                  mb-1
                "
              >
                Something went wrong
              </h1>

              <p
                className="
                  text-sm
                  text-slate-500
                  leading-relaxed
                  max-w-[430px]
                "
              >
                Something went wrong loading this page. Reload the page, or go
                back to the homepage.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6">
            {/* Refresh Page */}
            <button
              onClick={handleRefresh}
              className="
                w-full
                bg-transparent
                text-slate-400
                text-sm
                font-medium
                py-2
                cursor-pointer
                hover:text-slate-600
                transition-colors
              "
            >
              Reload Page
            </button>

            {/* Go To Homepage */}
            <button
              onClick={handleHome}
              className="
                w-full
                mt-2
                bg-white
                hover:bg-slate-50
                text-slate-900
                text-sm
                font-semibold
                py-3
                px-5
                rounded-lg
                border
                border-slate-200
                transition-all
                duration-200
                flex
                items-center
                justify-center
                gap-2
              "
            >
              <Home className="w-4 h-4" strokeWidth={2} />
              Go To Homepage
            </button>
          </div>

          {/* Support Section */}
          <div className="mt-5">
            {/* Divider */}
            <div className="h-px bg-slate-200 mb-4" />

            {/* Need Help */}
            <div className="text-center">
              <span className="text-xs sm:text-sm text-slate-500">
                Need help?{' '}
              </span>

              <a
                href="mailto:support@slc.digital"
                className="
                  text-xs
                  sm:text-sm
                  text-slate-800
                  font-medium
                  underline
                  hover:text-blue-600
                  transition-colors
                "
              >
                support@slc.digital
              </a>
            </div>
          </div>

          {/* Development Error Details */}
          {import.meta.env.MODE === 'development' && error && (
            <div className="mt-5 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs font-bold text-red-700 uppercase tracking-widest mb-2">
                Development: Error Details
              </p>

              <pre
                className="
                  text-xs
                  text-red-600
                  overflow-auto
                  max-h-32
                  font-mono
                  whitespace-pre-wrap
                  break-words
                  bg-red-100/50
                  p-2.5
                  rounded-md
                "
              >
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 text-center">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} SLC. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};
